import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { z } from "zod";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/lib/auth";
import { useLogin, usePrices } from "@/lib/hooks";
import { ApiError } from "@/lib/api";
import type { RegisterInput, CreateOrderInput } from "@/lib/api";
import { formatCurrency, formatPhone } from "@/lib/formatters";
import { usePaymentSubmission, type PreOrderOutcome } from "@/lib/payment-flow";
import {
  ATTESTATION_REQUIRED_MESSAGE,
  addCardIssues,
  addressSchema,
  cardSchema,
  employeeRowSchema,
  hasAttestedRow,
  toOrderCard,
} from "@/lib/checkout-form";
import { ErrorMessage } from "@/components/ErrorMessage";
import { EmployeeFields } from "@/components/EmployeeFields";
import { CompanyInfoFields } from "@/components/CompanyInfoFields";
import { PaymentFields } from "@/components/PaymentFields";
import { OrderSummary } from "@/components/OrderSummary";

// --- Zod schema ---

const checkoutSchema = z
  .object({
    employees: z
      .array(employeeRowSchema)
      .min(1, "At least one employee is required")
      .max(50, "Maximum 50 employees per order"),
    companyName: z.string().min(1, "Company name is required"),
    contactFirstName: z.string().min(1, "First name is required"),
    contactLastName: z.string().min(1, "Last name is required"),
    contactPhone: z.string().min(1, "Phone number is required"),
    contactEmail: z.string().email("Valid email is required"),
    billingAddress: addressSchema,
    mailingAddressSameAsBilling: z.boolean(),
    mailingAddress: z
      .object({
        street: z.string(),
        city: z.string(),
        state: z.string(),
        zip: z.string(),
      })
      .optional(),
    // Required, and true, only while at least one row is already-cleared. The
    // rule lives in superRefine below: under zodResolver an RHF `validate` rule
    // is never run.
    attestationAccepted: z.boolean(),
    isNewVendor: z.boolean(),
    email: z.string().email("Valid email is required"),
    password: z.string().optional(),
    confirmPassword: z.string().optional(),
    // Card-only while ACH is deferred (2026-07-22) — see
    // decisions/2026-07-22-defer-ach-card-only-rebuild.md.
    paymentMethod: z.enum(["credit_card"]),
    card: cardSchema.optional(),
  })
  .superRefine((data, ctx) => {
    // The attestation scopes to the rows marked already-cleared.
    if (hasAttestedRow(data.employees) && !data.attestationAccepted) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: ATTESTATION_REQUIRED_MESSAGE,
        path: ["attestationAccepted"],
      });
    }

    // Password validation for new vendors
    if (data.isNewVendor) {
      if (!data.password || data.password.length < 8) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Password must be at least 8 characters",
          path: ["password"],
        });
      }
      if (data.password !== data.confirmPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Passwords do not match",
          path: ["confirmPassword"],
        });
      }
    }

    // Credit card field validation
    addCardIssues(data.card, ctx);

    // Mailing address required when not same as billing
    if (!data.mailingAddressSameAsBilling) {
      if (!data.mailingAddress?.street) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Street is required",
          path: ["mailingAddress", "street"],
        });
      }
      if (!data.mailingAddress?.city) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "City is required",
          path: ["mailingAddress", "city"],
        });
      }
      if (!data.mailingAddress?.state || data.mailingAddress.state.length !== 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Select a state",
          path: ["mailingAddress", "state"],
        });
      }
      if (!data.mailingAddress?.zip || data.mailingAddress.zip.length < 5) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ZIP code is required",
          path: ["mailingAddress", "zip"],
        });
      }
    }
  });

export type CheckoutFormData = z.infer<typeof checkoutSchema>;

/** The order body for one submission under one key. Called for the first
 *  attempt and again, with the fresh key, for the bounded RETRY_NOW retry. */
function buildCheckoutOrder(data: CheckoutFormData, idempotencyKey: string): CreateOrderInput {
  const orderInput: CreateOrderInput = {
    type: "employee_addition",
    paymentMethod: "credit_card",
    employees: data.employees,
    idempotencyKey,
    billingAddress: data.billingAddress,
  };

  // The server stamps the attestation's copyVersion and time; the client sends
  // only the boolean, and only while a row is marked already-cleared. A mixed
  // batch is still ONE order and ONE payment.
  if (hasAttestedRow(data.employees) && data.attestationAccepted) {
    orderInput.attestationAccepted = true;
  }

  if (data.card) {
    orderInput.card = toOrderCard(data.card);
  }

  return orderInput;
}

export function CheckoutPage(): React.ReactElement {
  const navigate = useNavigate();
  const { vendor, isAuthenticated, register: authRegister, logout } = useAuth();
  const loginMutation = useLogin();
  const { data: priceData, isError: priceFailed } = usePrices();

  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState<string | null>(null);

  // No fallback price. The server charges its configured prices and the client
  // sends no amount, so a guessed figure on the button could differ from the
  // charge. Until the prices have loaded, the order cannot be placed.
  const [sessionEnded, setSessionEnded] = useState(false);

  const methods = useForm<CheckoutFormData>({
    resolver: zodResolver(checkoutSchema),
    defaultValues: {
      employees: [{ firstName: "", lastName: "", product: "bcb" }],
      attestationAccepted: false,
      companyName: "",
      contactFirstName: "",
      contactLastName: "",
      contactPhone: "",
      contactEmail: "",
      billingAddress: { street: "", city: "", state: "", zip: "" },
      mailingAddressSameAsBilling: true,
      mailingAddress: { street: "", city: "", state: "", zip: "" },
      isNewVendor: true,
      email: "",
      password: "",
      confirmPassword: "",
      paymentMethod: "credit_card",
      card: {
        cardNumber: "",
        expireMonth: "",
        expireYear: "",
        cvv: "",
        nameOnCard: "",
      },
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    getValues,
    reset,
    formState: { errors, isSubmitting },
  } = methods;

  const hasValidationErrors = Object.keys(errors).length > 0;

  const employees = watch("employees");
  const isNewVendor = watch("isNewVendor");
  const badgeCount = (employees ?? []).filter((e) => e.product === "badge_attested").length;
  const bcbCount = (employees?.length ?? 1) - badgeCount;
  const total =
    priceData === undefined
      ? undefined
      : bcbCount * priceData.bcbPrice + badgeCount * priceData.badgePrice;

  // Pre-fill from the stored vendor record — but ONLY into fields still empty.
  // A vendor can log in mid-checkout (the EMAIL_EXISTS path), after typing a
  // new card's billing address; overwriting it would send the stored ZIP to
  // Vanco with the new card. What they typed always wins.
  useEffect(() => {
    if (!vendor) return;
    setSessionEnded(false);
    setValue("isNewVendor", false);
    setValue("email", vendor.email);
    setValue("contactEmail", vendor.email);
    const current = getValues();
    const fillIfEmpty = (
      name: "companyName" | "contactFirstName" | "contactLastName" | "contactPhone",
      value: string,
    ): void => {
      if (!current[name]) setValue(name, value);
    };
    fillIfEmpty("companyName", vendor.companyName);
    fillIfEmpty("contactFirstName", vendor.contactFirstName);
    fillIfEmpty("contactLastName", vendor.contactLastName);
    fillIfEmpty("contactPhone", formatPhone(vendor.contactPhone));
    const isBlank = (a?: { street: string; city: string; state: string; zip: string }): boolean =>
      !a || (!a.street && !a.city && !a.state && !a.zip);
    if (isBlank(current.billingAddress)) {
      setValue("billingAddress", vendor.billingAddress);
    }
    if (
      vendor.mailingAddress &&
      current.mailingAddressSameAsBilling &&
      isBlank(current.mailingAddress)
    ) {
      setValue("mailingAddressSameAsBilling", false);
      setValue("mailingAddress", vendor.mailingAddress);
    }
  }, [vendor, setValue, getValues]);

  // The session ended while this page was open (expiry, or a rejected refresh).
  // The form still says "existing vendor" and has no password fields, so a
  // submit could only fail. Open the login form with their address instead;
  // everything they typed stays. An explicit Logout resets the form and does
  // not come through here (see handleLogout).
  const previousVendorRef = useRef(vendor);
  const loggedOutByChoiceRef = useRef(false);
  useEffect(() => {
    const previous = previousVendorRef.current;
    previousVendorRef.current = vendor;
    if (!previous || vendor) return;
    if (loggedOutByChoiceRef.current) {
      loggedOutByChoiceRef.current = false;
      return;
    }
    setLoginEmail(previous.email);
    setShowLoginForm(true);
    setSessionEnded(true);
  }, [vendor]);

  function handleLogout(): void {
    loggedOutByChoiceRef.current = true;
    reset(); // the next person at this tab must not inherit this vendor's details
    logout();
  }

  // Sync contactEmail with email
  const emailValue = watch("email");
  useEffect(() => {
    if (emailValue) {
      setValue("contactEmail", emailValue);
    }
  }, [emailValue, setValue]);

  async function handleLogin(): Promise<void> {
    setLoginError(null);
    try {
      await loginMutation.mutateAsync({
        email: loginEmail,
        password: loginPassword,
      });
      setShowLoginForm(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setLoginError(err.message);
      } else {
        setLoginError("Login failed. Please try again.");
      }
    }
  }

  /** The public checkout's step before the order: the session-ended refusal and
   *  registration. Runs under the payment hook's submit latch, and never on its
   *  RETRY_NOW retry — registration must not repeat under a stale auth closure
   *  (F24). Any error other than EMAIL_EXISTS goes to the hook's shared tail. */
  async function beforeOrder(data: CheckoutFormData): Promise<PreOrderOutcome> {
    if (!isAuthenticated && !data.isNewVendor) {
      // An existing vendor whose session ended: the order request would be
      // sent with no token and can only fail. No charge is possible here.
      setShowLoginForm(true);
      setSessionEnded(true);
      return { proceed: false, message: "Please log in to place this order." };
    }
    // Register new vendor first (skip if already authenticated)
    if (!isAuthenticated && data.isNewVendor) {
      const registerInput: RegisterInput = {
        email: data.email,
        password: data.password ?? "",
        companyName: data.companyName,
        contactFirstName: data.contactFirstName,
        contactLastName: data.contactLastName,
        contactPhone: data.contactPhone.replace(/\D/g, ""),
        billingAddress: data.billingAddress,
      };
      if (!data.mailingAddressSameAsBilling && data.mailingAddress) {
        registerInput.mailingAddress = data.mailingAddress;
      }
      try {
        await authRegister(registerInput);
      } catch (err) {
        if (err instanceof ApiError && err.code === "EMAIL_EXISTS") {
          // Precedes tokenize, so no charge can exist -- this is purely about
          // not stranding a vendor who already has an account. Open the login
          // form and carry the address across rather than telling them to find
          // it. The form state is untouched, so logging in and resubmitting
          // keeps everything they typed.
          setShowLoginForm(true);
          setLoginEmail(data.email);
          setError("email", {
            message: "An account with this email already exists. Log in above to continue.",
          });
          return { proceed: false };
        }
        throw err;
      }
    }
    return { proceed: true };
  }

  const payment = usePaymentSubmission<CheckoutFormData>({
    flow: "public",
    buildOrder: buildCheckoutOrder,
    onSuccess: (result) => {
      void navigate("/confirmation", {
        state: {
          order: result.order,
          employees: result.employees,
        },
      });
    },
    beforeOrder,
  });

  return (
    <div className="mx-auto max-w-[640px] px-4 py-8">
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900">VendorPay</h1>
        <p className="mt-1 text-sm text-gray-500">
          Diocese of Pensacola-Tallahassee
        </p>
        <p className="mt-1 text-sm text-gray-600">
          Background Check &amp; Badge Portal
        </p>
      </div>

      {/* Auth banner */}
      {isAuthenticated && vendor ? (
        <div className="mb-6 flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-4 py-3">
          <span className="text-sm text-blue-800">
            Welcome back, {vendor.email}
          </span>
          <div className="flex gap-3">
            <Link
              to="/orders"
              className="text-sm font-medium text-blue-700 hover:text-blue-900"
            >
              Receipts
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="text-sm font-medium text-blue-700 hover:text-blue-900"
            >
              Logout
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-6">
          {!showLoginForm ? (
            <p className="text-sm text-gray-600">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => setShowLoginForm(true)}
                className="font-medium text-blue-700 hover:text-blue-900"
              >
                Log in
              </button>
            </p>
          ) : (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">
                Log in to your account
              </h3>
              {sessionEnded && (
                <p className="mb-2 text-sm text-gray-700">
                  Your session has ended. Log in to continue — everything you
                  entered is still here.
                </p>
              )}
              <ErrorMessage message={loginError} />
              <div className="mt-2 space-y-3">
                <div>
                  <label
                    htmlFor="login-email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="login-password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                  <input
                    id="login-password"
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => void handleLogin()}
                    disabled={loginMutation.isPending}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loginMutation.isPending ? "Logging in..." : "Log In"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowLoginForm(false);
                      setLoginError(null);
                    }}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <FormProvider {...methods}>
        <form
          onSubmit={(e) =>
            void handleSubmit(payment.submit, (formErrors) => {
              if (import.meta.env.DEV) {
                console.log("Form validation errors:", formErrors);
              }
              // Scroll to first error field on validation failure
              requestAnimationFrame(() => {
                const firstError = document.querySelector(
                  "[class*='border-red-500']",
                );
                if (firstError) {
                  firstError.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
              });
            })(e)
          }
          noValidate
          className="space-y-8"
        >
          {/* Employees */}
          <section>
            <EmployeeFields />
          </section>

          {/* Company Info */}
          <section>
            <CompanyInfoFields />
          </section>

          {/* Account section for new vendors */}
          {!isAuthenticated && (
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Your Account
              </h3>
              <p className="text-sm text-gray-600">
                We will create an account so you can track your order status and
                place future orders.
              </p>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  {...register("email")}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.email
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300"
                  }`}
                />
                {errors.email?.message && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {isNewVendor && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      autoComplete="new-password"
                      aria-describedby="password-hint"
                      {...register("password")}
                      className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.password
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    <p id="password-hint" className="mt-1 text-xs text-gray-500">
                      Must be at least 8 characters.
                    </p>
                    {errors.password?.message && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Confirm Password
                    </label>
                    <input
                      id="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      {...register("confirmPassword")}
                      className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.confirmPassword
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {errors.confirmPassword?.message && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Payment */}
          <section>
            <PaymentFields />
          </section>

          {/* Order Summary */}
          <section>
            <OrderSummary bcbCount={bcbCount} badgeCount={badgeCount} />
          </section>

          {/* Validation errors summary */}
          {hasValidationErrors && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <p className="font-medium">Please fix the following errors:</p>
              <ul className="mt-1 list-inside list-disc">
                {Object.entries(errors).map(([key, val]) => (
                  <li key={key}>
                    {key}: {val && typeof val === "object" && "message" in val
                      ? String(val.message)
                      : "has errors"}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Submit error */}
          {payment.submitError && (
            <ErrorMessage message={payment.submitError} />
          )}

          {priceFailed && (
            <ErrorMessage message="We could not load the current price, so the order cannot be placed yet. Please reload the page to try again." />
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting || payment.isHeld || total === undefined}
            className="w-full rounded-md bg-blue-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting
              ? "Processing..."
              : total === undefined
                ? "Place Order"
                : `Place Order \u2014 ${formatCurrency(total)}`}
          </button>
        </form>
      </FormProvider>
    </div>
  );
}
